import { useRef, useState } from 'react'
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useAccountType } from '@/hooks/useAccountType'
import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { captureRequestScope } from '@/lib/api/request-scope'
import { readSidebarPreset } from '@/lib/navigation/sidebar-presets'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { toast } from 'sonner'

import { SessionChangedError } from '@scrambl/shared/session-scope'

export function SidebarPresetMenu() {
  const { isPersonal } = useAccountType()
  const organization = useOrganizationStore(state => state.currentOrganization)
  const userId = useAuth(state => state.user?.id)
  if (
    isPersonal ||
    !userId ||
    !organization ||
    organization.isExternalAccess ||
    organization.userMembership?.status !== 'ACTIVE'
  )
    return null
  return (
    <SidebarPresetOptions key={JSON.stringify([userId, organization.id])} />
  )
}

function SidebarPresetOptions() {
  const { preferences, setSidebarPreset, isInitialized, isLoading, error } =
    useNavigationPreferences()
  const [saving, setSaving] = useState(false)
  const pending = useRef(false)

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="px-3 text-xs text-[hsl(var(--sidebar-text-muted))]">
        Pages in your sidebar
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        aria-label="Pages in your sidebar"
        value={readSidebarPreset(preferences.sidebarPreset)}
        onValueChange={value => {
          if (pending.current || !isInitialized || isLoading) return
          const request = captureRequestScope()
          pending.current = true
          setSaving(true)
          void setSidebarPreset(readSidebarPreset(value))
            .catch((failure: unknown) => {
              if (
                request.signal.aborted ||
                failure instanceof SessionChangedError
              )
                return
              toast.error('Sidebar preset couldn’t be saved. Please try again.')
            })
            .finally(() => {
              pending.current = false
              setSaving(false)
            })
        }}
      >
        <DropdownMenuRadioItem
          className="rounded-xl py-2.5 text-[hsl(var(--sidebar-text))] focus:bg-[hsl(var(--sidebar-text)/0.1)] focus:text-[hsl(var(--sidebar-text))] data-[state=checked]:bg-[hsl(var(--sidebar-text)/0.18)]"
          value="default"
          disabled={saving || isLoading || !isInitialized}
        >
          Standard
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem
          className="rounded-xl py-2.5 text-[hsl(var(--sidebar-text))] focus:bg-[hsl(var(--sidebar-text)/0.1)] focus:text-[hsl(var(--sidebar-text))] data-[state=checked]:bg-[hsl(var(--sidebar-text)/0.18)]"
          value="small-team"
          disabled={saving || isLoading || !isInitialized}
        >
          Small team
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
      <p className="px-3 py-2 text-xs leading-relaxed text-[hsl(var(--sidebar-text-muted))]">
        Small team keeps clients, projects, tasks, time, and invoices close.
        Other pages stay under More. Your show/hide choices and pins are kept.
        Only affects your sidebar in this workspace.
      </p>
      {(saving || !isInitialized) && (
        <p
          role={error ? 'alert' : 'status'}
          className="px-3 pb-2 text-xs text-[hsl(var(--sidebar-text-muted))]"
        >
          {saving
            ? 'Saving…'
            : error
              ? 'Preferences couldn’t be loaded. Reopen the workspace to retry.'
              : 'Loading preferences…'}
        </p>
      )}
    </>
  )
}
